import {useTranslation} from "react-i18next";
import React, {useState} from "react";
import endOfToday from 'date-fns/endOfToday';
import startOfToday from 'date-fns/endOfToday';
import subMinutes from 'date-fns/subMinutes';
import {useMutation, useQuery} from "@apollo/client";
import {useTheme} from "@mui/material/styles";
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    Box,
    Collapse,
    IconButton,
    Stack, TextFieldProps,
    Typography,
    useMediaQuery
} from "@mui/material";
import {format} from "date-fns";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import CurrencyFormatter from "../../../../components/CurrencyFormatter/CurrencyFormatter";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import frLocale from "date-fns/locale/fr";
import TextInput from "../../../../components/TextInput/TextInput";
import {addDays, addMonths, addWeeks, endOfMonth,endOfDay,  startOfDay} from "date-fns/fp";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import DatePicker from "@mui/lab/DatePicker";
import {UseQuerySuspense} from "../../../../components/UseQuerySuspense/UseQuerySuspense";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import {GET_PAYMENT_HISTORY} from "../../../../store/queries/GetPaymentHistory";
import {TransactionsTable} from "./components/TransactionsTable";
import {paymentMethodIcon} from "../deposit/components/PaymentMethod";
import {LoadingButton} from "@mui/lab";
import {CANCEL_WITHDRAWAL_MUTATION} from "../../../../store/mutations/CancelWithdrawal";
import {GET_USER} from "../../../../store/queries/GetUser";
import {
    CancelWithdrawalMutationMutation,
    CancelWithdrawalMutationMutationVariables,
    GetPaymentHistoryQuery, GetPaymentHistoryQueryVariables, PaymentTx,
    PaymentTxStatus,
    PaymentTxType
} from "../../../../gql/graphql";

export default () => {
    const {t} = useTranslation();
    const [state, setState] = useState({period: t('today'), start: startOfDay(new Date()), end: endOfDay(new Date())});
    const transactionHistory = useQuery<GetPaymentHistoryQuery, GetPaymentHistoryQueryVariables>(GET_PAYMENT_HISTORY, {
        variables: {
            after: null,
            before: null,
            first: 25,
            where: {
                and: [{timeStamp: {gte: state.start}},
                    {lastStatusUpdate: {lte: state.end}}]
            }
        },
        fetchPolicy: 'network-only',
        errorPolicy:'all'
    });
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const Row = (props: { row: PaymentTx }) => {
        const {row} = props;
        const [open, setOpen] = React.useState(false);
        const [commitCancelWithdrawal, cancelWithdrawalState] = useMutation<CancelWithdrawalMutationMutation,CancelWithdrawalMutationMutationVariables>(CANCEL_WITHDRAWAL_MUTATION,
            {
                update(cache, {data}) {
                    cache.writeQuery({
                        data: {identity: data?.cancelWithdrawal},
                        query: GET_USER
                    });
                }
            });
        const cancelWithdrawal = () => {
            commitCancelWithdrawal({
                variables: {
                    withdrawalId: row.id
                },
                onCompleted(data){
                    (window as any).dataLayer = (window as any).dataLayer || [];
                    (window as any).dataLayer.push({
                        'event': 'cancelWithdrawSuccess',
                        'value': row.amount,
                        'currency' : 'EUR'
                    });
                    transactionHistory.refetch();
                },
                onError(data) {

                }
            })
        };
        let startDate = format(new Date(row.timeStamp), 'yyyy-MM-dd HH:mm:ss');
        let endDate = format(subMinutes(new Date(row.lastStatusUpdate), 15), 'yyyy-MM-dd HH:mm:ss');
        return (
            <React.Fragment>
                <TableRow sx={{'& > *': {borderBottom: 'unset'}}}>
                    <TableCell>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => setOpen(!open)}
                        >
                            {open ? <KeyboardArrowUp/> : <KeyboardArrowDown/>}
                        </IconButton>
                    </TableCell>
                    <TableCell>
                        {startDate}
                    </TableCell>
                    <TableCell align="left">
                        {row?.type}
                    </TableCell>
                    <TableCell align="right">

                        {row.iban && (<>{t('bankTransfer')}</>)}
                        {row.product && (<>{paymentMethodIcon(row.product.name, true)}</>)}
                    </TableCell>
                    <TableCell align="right">
                        <CurrencyFormatter currency={'EUR'} quantity={row.amount}/>
                    </TableCell>
                    <TableCell align="right">
                        {row.status}
                    </TableCell>
                    <TableCell>
                        {row.type == PaymentTxType.Withdrawal && row.status == PaymentTxStatus.Started && (
                            <LoadingButton loading={cancelWithdrawalState.loading} onClick={cancelWithdrawal}>{t('cancel')}</LoadingButton>
                        )}
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell sx={{p:0}}/>
                    <TableCell sx={{paddingBottom: 0, paddingTop: 0,pl:1,pr:0}} colSpan={6}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{margin: 1}}>

                                <Typography variant="h6" gutterBottom component="div">
                                    {t('details')}
                                </Typography>
                                <Table size={'small'} sx={{width:'auto'}}>
                                    <TableBody  sx={{'& tr td': { border: 0 }}}>
                                        <TableRow>
                                            <TableCell align={'right'} sx={{fontWeight:700}}>
                                                {t('id')}
                                            </TableCell>
                                            <TableCell>
                                                {row.id}
                                            </TableCell>
                                        </TableRow>
                                        {row.iban && (
                                            <TableRow>
                                            <TableCell align={'right'} sx={{fontWeight:700}}>
                                                {t('bankAccount')}
                                            </TableCell>
                                            <TableCell>
                                                {row.iban}
                                            </TableCell>
                                                
                                        </TableRow>)}
                                        
                                    </TableBody>
                                </Table>
                                <Typography variant="h6" gutterBottom component="div">
                                    {t('transactions')}
                                </Typography>
                                <TransactionsTable paymentTx={props.row}/>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );

    }


    return (<Box sx={{p: {xs:2,md:3}, maxWidth: 1, display:'flex'}}>
        <Stack spacing={3} direction={'column'}>
        <Typography variant={'h5'} sx={{pb: 3 ,display:'flex', flexDirection:'row', alignItems:'center', minHeight:64}}>{t('transactionHistory')}</Typography>

        <TableContainer component={Paper} sx={{px: 2,maxWidth:1}}>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={frLocale}>

                <Stack direction={isSmall?'column':'row'} spacing={3} sx={{px: 1, py: 3}}>
                    <Autocomplete
                        disablePortal
                        options={[t('today'), t('yesterday'), t('thisWeek'), t('lastWeek'), t('thisMonth'), t('lastMonth'), t('custom')]}
                        fullWidth
                        value={state.period}
                        isOptionEqualToValue={(option, value) => option == value}
                        renderInput={(params: AutocompleteRenderInputParams) =>
                            <TextInput {...params}
                                       sx={{m: 0}}
                                       fullWidth
                                       label={t('period')}/>}
                        getOptionLabel={(option) => (option)}
                        onChange={(_, data) => {
                            switch (data) {
                                case t('today'):
                                    setState({start: startOfToday(), end: endOfToday(), period: data as string});
                                    break;
                                case t('yesterday'):
                                    setState({
                                        start: addDays(-1, startOfToday()),
                                        end: addDays(-1, endOfToday()),
                                        period: data as string
                                    });
                                    break;
                                case t('thisWeek'):
                                    setState({
                                        start: startOfWeek(startOfToday(),{weekStartsOn:1}),
                                        end: endOfToday(),
                                        period: data as string
                                    });
                                    break;
                                case t('lastWeek'):
                                    setState({
                                        start: addWeeks(-1,startOfWeek( startOfToday(),{weekStartsOn:1})),
                                        end: addWeeks(-1,endOfWeek( endOfToday(),{weekStartsOn:1})),
                                        period: data as string
                                    });

                                    break;
                                case t('thisMonth'):
                                    setState({
                                        start: startOfMonth(startOfToday()),
                                        end: endOfToday(),
                                        period: data as string
                                    });
                                    break;
                                case t('lastMonth'):
                                    setState({
                                        start: (startOfMonth(addMonths(-1, startOfToday()))),
                                        end: endOfMonth(addMonths(-1, endOfToday())),
                                        period: data as string
                                    });
                                    break;

                                case t('custom'):
                                    setState({
                                        ...state,
                                        period: data as string
                                    });
                                    break;
                            }
                        }}
                    />
                    <DatePicker
                        openTo={'day'}
                        views={['year', 'month', 'day']}
                        value={state.start}
                        onChange={(newvalue: Date| null) => {
                            setState({...state, period:t('custom'),start: startOfDay(newvalue as Date)});
                        }}
                        label={t('start')}
                        maxDate={new Date()}
                        renderInput={(params: TextFieldProps) =>
                            <TextInput fullWidth sx={{m: 0}} {...params}/>}/>
                    <DatePicker
                        openTo={'day'}
                        views={['year', 'month', 'day']}
                        value={state.end}
                        onChange={(newvalue: Date| null) => {
                            setState({...state, period:t('custom'), end: endOfDay(newvalue as Date)});
                        }}
                        label={t('end')}
                        maxDate={new Date()}
                        renderInput={(params: TextFieldProps) => <TextInput fullWidth {...params}/>}/>
                </Stack>
            </LocalizationProvider>
            <UseQuerySuspense data={transactionHistory}>
                <TableContainer sx={{maxWidth:1}}>
                    <Table sx={{mt: 1, px: 3}}>
                        <TableHead sx={{
                            '& > tr > th': {
                                borderBottom: 'none',
                                backgroundColor: '#F4F6F8',
                                fontWeight: 600,
                                whiteSpace: 'nowrap'
                            },
                            '& > tr > th:first-of-type': {
                                pl: 3,
                                borderTopLeftRadius: '8px',
                                borderBottomLeftRadius: '8px',
                                boxShadow: 'inset 8px 0 0 #fff'
                            },
                            '& > tr > th:last-of-type': {
                                pr: 3,
                                borderTopRightRadius: '8px',
                                borderBottomRightRadius: '8px',
                                boxShadow: 'inset -8px 0 0 #fff'
                            }
                        }}>
                            <TableRow>
                                <TableCell/>
                                <TableCell>{t('timestamp')}</TableCell>
                                <TableCell align="left">{t('type')}</TableCell>
                                <TableCell align="right">{t('method')}</TableCell>
                                <TableCell align="right">{t('amount')}</TableCell>
                                <TableCell align="right">{t('status')}</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableHead>

                        <TableBody sx={{
                            '& > tr > td': {
                                borderBottom: 'none'
                            }
                        }}>
                            {transactionHistory.data?.paymentHistory?.nodes?.map((value, index) => {

                                return (<Row key={index} row={value as PaymentTx}/>)
                            })}
                        </TableBody>

                    </Table>
                </TableContainer>
                <Stack direction={'row'} justifyContent={'flex-end'}>
                    <IconButton disabled={!transactionHistory.data?.paymentHistory?.pageInfo.hasPreviousPage} onClick={() => {
                        transactionHistory.fetchMore({
                            variables: {
                                before: transactionHistory.data?.paymentHistory?.pageInfo.startCursor,
                                first: null,
                                last: 25,
                            }
                        });
                    }}>
                        <KeyboardArrowLeft/>
                    </IconButton>
                    <IconButton disabled={!transactionHistory.data?.paymentHistory?.pageInfo.hasNextPage} onClick={() => {
                        transactionHistory.fetchMore({
                            variables: {
                                after: transactionHistory.data?.paymentHistory?.pageInfo.endCursor,
                                first: 25,
                                last: null
                            }
                        });
                    }}>
                        <KeyboardArrowRight/>
                    </IconButton>
                </Stack>
            </UseQuerySuspense>
        </TableContainer>
        </Stack>
    </Box>)
}